import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/Button';
import styles from './RouteErrorBoundary.module.scss';

// Rendered outside any <Suspense> (see index.tsx) and possibly because translations failed to load, so it
// must never suspend; the English defaults below cover a missing namespace.
const RouteErrorFallback = () => {
    const { t } = useTranslation(undefined, { useSuspense: false });
    return (
        <div className={styles.fallback} role="alert">
            <p>{t('RouteError.Message', 'Something went wrong loading this page.')}</p>
            <Button color="blue" type="button" onClick={() => window.location.reload()}>
                {t('RouteError.Reload', 'Reload')}
            </Button>
        </div>
    );
};

interface RouteErrorBoundaryProps {
    children: ReactNode;
}

interface RouteErrorBoundaryState {
    hasError: boolean;
}

/** Catches render and lazy-chunk load errors for one route so it shows a message instead of a blank page. */
export class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
    state: RouteErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): RouteErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error(error, info.componentStack);
    }

    render() {
        return this.state.hasError ? <RouteErrorFallback /> : this.props.children;
    }
}
